import React from 'react';
import { Alert, DatePicker, Form, Input, Modal, Spin, Typography } from 'antd';
import { DOCUMENT_UI_DATETIME_FORMAT } from '../../../../utils/documentUiFormat';
import { CLOSING_PACKAGE_MODAL_TITLES } from '../../workspace/closingPackageModalKinds';
import { useRevisionModal } from '../../hooks/useRevisionModal';
import ClosingPackageCompositionEditor from '../ClosingPackageCompositionEditor';
import ClosingPackageTaskModalContext from './ClosingPackageTaskModalContext';
import ClosingPackageTaskModalEventDateHint from './ClosingPackageTaskModalEventDateHint';
import ClosingPackageTaskModalFooter from './ClosingPackageTaskModalFooter';
import type { ClosingPackageTaskModalBaseProps } from './ClosingPackageFormationModal.types';

const { Text } = Typography;

export type ClosingPackageRevisionModalProps = ClosingPackageTaskModalBaseProps & {
  onSuccess?: () => void | Promise<void>;
};

/**
 * IMP-001H-4 — REVISE_CLOSING_PACKAGE workspace modal (composition editor + confirmFormation).
 */
const ClosingPackageRevisionModal: React.FC<ClosingPackageRevisionModalProps> = ({
  open,
  onClose,
  applicationId,
  onSuccess,
}) => {
  const {
    form,
    projection,
    contextView,
    contextLoading,
    rejectionReasonView,
    submitting,
    handleSubmit,
  } = useRevisionModal({
    applicationId,
    open,
    onClose,
    onSuccess,
  });

  return (
    <Modal
      title={CLOSING_PACKAGE_MODAL_TITLES.revision}
      open={open}
      onCancel={onClose}
      destroyOnClose
      width="min(1440px, calc(100vw - 32px))"
      style={{ top: 16 }}
      styles={{ body: { maxHeight: 'calc(100vh - 150px)', overflowY: 'auto' } }}
      className="cp-task-modal"
      data-testid="revision-modal"
      footer={
        <ClosingPackageTaskModalFooter
          onClose={onClose}
          onSubmit={handleSubmit}
          submitting={submitting}
          submitLabel="Подтвердить доработку"
          submitTestId="revision-submit"
        />
      }
    >
      {contextLoading || !projection ? (
        <div style={{ textAlign: 'center', padding: 32 }}>
          <Spin />
        </div>
      ) : (
        <>
          <ClosingPackageTaskModalContext contextView={contextView} />

          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Причина доработки"
            description={
              <Text data-testid="revision-rejection-reason">{rejectionReasonView.text}</Text>
            }
          />

          <Form form={form} layout="vertical">
            <Form.Item
              name="revisedAt"
              label="Дата выполнения доработки"
              rules={[{ required: true, message: 'Укажите дату выполнения доработки' }]}
            >
              <DatePicker
                showTime
                style={{ width: '100%' }}
                data-testid="revision-date"
                format={DOCUMENT_UI_DATETIME_FORMAT}
              />
            </Form.Item>

            <Form.Item label="Состав комплекта" required>
              <div
                className="cp-task-modal__composition-shell"
                data-testid="revision-modal-composition-editor"
              >
                <ClosingPackageCompositionEditor projection={projection} />
              </div>
            </Form.Item>

            <Form.Item
              name="comment"
              label="Комментарий"
            >
              <Input.TextArea
                rows={3}
                maxLength={500}
                showCount
                placeholder="Комментарий для журнала комплекта (необязательно)"
                data-testid="revision-comment"
              />
            </Form.Item>
          </Form>

          <ClosingPackageTaskModalEventDateHint />
        </>
      )}
    </Modal>
  );
};

export default ClosingPackageRevisionModal;
